import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Markdown from '../Markdown';
import Iconify from '../Iconify';
import { useDispatch } from '../../redux/store';
import { clearLatestMessage } from '../../redux/slices/aiConversation';
// ----------------------------------------------------------------------

const animatedMessageIds = new Set();

function useTypewriter(text, isLatest, onScrollBottom, onComplete) {
  const [displayedLength, setDisplayedLength] = useState(isLatest ? 0 : text?.length || 0);

  useEffect(() => {
    if (!isLatest || !text) {
      setDisplayedLength(text?.length || 0);
      return undefined;
    }

    setDisplayedLength(0);
    const totalChars = text.length;
    const step = Math.max(4, Math.ceil(totalChars / 35));
    const interval = setInterval(() => {
      setDisplayedLength((prev) => {
        const next = prev + step;
        if (next >= totalChars) {
          clearInterval(interval);
          if (onScrollBottom) {
            onScrollBottom(true);
          }
          if (onComplete) {
            onComplete();
          }
          return totalChars;
        }
        if (onScrollBottom) {
          onScrollBottom(true);
        }
        return next;
      });
    }, 25);

    return () => clearInterval(interval);
  }, [text, isLatest, onScrollBottom, onComplete]);

  return isLatest && text ? text.slice(0, displayedLength) : text;
}

function getMessage({ message }) {
  const sender = message.sender_id === 0 ? { name: 'Shahidi Assistant' } : { name: 'User' };

  const senderDetails =
    message.sender_id !== 0
      ? { type: 'me' }
      : { avatarUrl: sender?.avatarUrl, firstName: sender?.name?.split(' ')[0] ?? 'Unknown' };

  const hasImage = message.contentType === 'image';

  return { hasImage, me: message.sender_id !== 0, senderDetails };
}

ChatMessageItem.propTypes = {
  message: PropTypes.object,
  onOpenLightbox: PropTypes.func,
  onScrollBottom: PropTypes.func
};

export function ChatMessageItem({ message, onOpenLightbox, onScrollBottom }) {
  const dispatch = useDispatch();
  const { me, senderDetails, hasImage } = getMessage({
    message
  });

  let messageKey = null;
  if (message?.id) {
    messageKey = String(message.id);
  } else if (message?.body) {
    messageKey = `${message.sender_id}-${message.created_at}-${message.body.slice(0, 20)}`;
  }

  const isAlreadyAnimated = messageKey ? animatedMessageIds.has(messageKey) : false;
  const shouldAnimate = Boolean(message?.isLatest && !me && !isAlreadyAnimated && !message?.isStreaming);
  const [isTypingDone, setIsTypingDone] = useState(!shouldAnimate);

  const handleAnimationComplete = useCallback(() => {
    setIsTypingDone(true);
    if (messageKey) {
      animatedMessageIds.add(messageKey);
      dispatch(clearLatestMessage({ messageId: message?.id }));
    }
  }, [dispatch, message?.id, messageKey]);

  useEffect(
    () => () => {
      if (messageKey && message?.isLatest) {
        animatedMessageIds.add(messageKey);
      }
    },
    [messageKey, message?.isLatest]
  );

  const contentBody = useTypewriter(message?.body, shouldAnimate, onScrollBottom, handleAnimationComplete);

  const renderInfo = () => (
    <Typography noWrap variant="caption" sx={{ mb: 1, color: 'text.disabled', ...(!me && { mr: 'auto' }) }}>
      {!me && `${senderDetails.firstName}, `}
      {message?.created_at || (message?.isStreaming ? 'En train d’écrire...' : '')}
    </Typography>
  );

  const renderSources = () => {
    if (me || !message?.sources || message.sources.length === 0) {
      return null;
    }
    if ((shouldAnimate && !isTypingDone) || message?.isStreaming) {
      return null;
    }

    return (
      <Box
        sx={{
          mt: 1.5,
          pt: 1.25,
          borderTop: (theme) => `1px dashed ${theme.palette.divider}`,
          width: '100%'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1, color: 'text.secondary' }}>
          <Iconify icon="eva:book-open-outline" width={16} height={16} />
          <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Sources documentaires ({message.sources.length})
          </Typography>
        </Stack>

        <Stack direction="row" flexWrap="wrap" gap={0.75}>
          {message.sources.map((source, idx) => {
            const label = source.reference ? `${source.title} (${source.reference})` : source.title;

            return (
              <Chip
                key={`source-${idx}-${source.title}`}
                size="small"
                variant="outlined"
                icon={
                  <Iconify
                    icon={source.url ? 'eva:external-link-outline' : 'eva:file-text-outline'}
                    width={14}
                    height={14}
                  />
                }
                label={label}
                component={source.url ? 'a' : 'div'}
                href={source.url || undefined}
                target={source.url ? '_blank' : undefined}
                rel={source.url ? 'noopener noreferrer' : undefined}
                clickable={Boolean(source.url)}
                sx={{
                  fontSize: '0.75rem',
                  maxWidth: '100%',
                  bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'grey.800' : 'background.paper'),
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: (theme) => {
                      if (!source.url) return undefined;
                      return theme.palette.mode === 'dark' ? 'grey.700' : 'grey.100';
                    },
                    borderColor: source.url ? 'primary.main' : 'divider'
                  }
                }}
              />
            );
          })}
        </Stack>
      </Box>
    );
  };

  const renderBody = () => (
    <Stack
      sx={{
        p: 1.5,
        minWidth: 48,
        maxWidth: { xs: 320, sm: 540, md: 660 },
        borderRadius: 1,
        typography: 'body2',
        bgcolor: 'background.neutral',
        ...(me && { color: 'grey.800', bgcolor: 'primary.lighter' }),
        ...(hasImage && { p: 0, bgcolor: 'transparent' })
      }}
    >
      {hasImage ? (
        <Box
          component="img"
          alt="Attachment"
          src={message?.body}
          onClick={() => onOpenLightbox(message?.body)}
          sx={{
            width: 400,
            height: 'auto',
            borderRadius: 1.5,
            cursor: 'pointer',
            objectFit: 'cover',
            aspectRatio: '16/11',
            '&:hover': { opacity: 0.9 }
          }}
        />
      ) : (
        <>
          {message?.isStreaming ? (
            <Box component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {message.body}
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 7,
                  height: 14,
                  ml: 0.5,
                  verticalAlign: 'middle',
                  bgcolor: 'primary.main',
                  animation: 'streamingBlink 0.9s infinite',
                  '@keyframes streamingBlink': {
                    '0%': { opacity: 0.1 },
                    '50%': { opacity: 1 },
                    '100%': { opacity: 0.1 }
                  }
                }}
              />
            </Box>
          ) : (
            <Markdown children={contentBody} />
          )}
          {renderSources()}
        </>
      )}
    </Stack>
  );

  if (!message?.body && !message?.isStreaming) {
    return null;
  }

  return (
    <Box
      data-message-id={message?.id}
      data-message-sender={me ? 'user' : 'assistant'}
      sx={{ mb: 5, display: 'flex', justifyContent: me ? 'flex-end' : 'unset' }}
    >
      {!me && (
        <Avatar alt={senderDetails.firstName} sx={{ width: 32, height: 32, mr: 2 }}>
          <AutoAwesomeIcon fontSize="small" />
        </Avatar>
      )}

      <Stack alignItems={me ? 'flex-end' : 'flex-start'}>
        {renderInfo()}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative'
          }}
        >
          {renderBody()}
        </Box>
      </Stack>
    </Box>
  );
}
